import DocumentDrawer from "@/components/library-drawers/document-drawer";
import LinkDrawer, {
  OpenChangeData,
} from "@/components/library-drawers/link-drawer";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useAddEvidenceToQuizResponseMutation } from "@/hooks/add-evidence-to-quiz-response.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useQuestion } from "@/store/question.context";
import { DimensionId } from "@/types/questionnaries";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Book,
  Paperclip2,
  SearchNormal1,
  SearchZoomIn,
  Trash,
} from "iconsax-react";
import { FC, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { evidenceSchema } from "./evidence.schema";
import MiniLibrary, { MiniLibraryOpenChangeData } from "./mini-library";
import ResourceDetail from "./resource-detail";

type NewEvidenceDrawerProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  responseId: string;
};

type Resource = z.infer<typeof evidenceSchema>["resources"][number];

const initialResources: Resource[] = [];

const NewEvidenceDrawer: FC<NewEvidenceDrawerProps> = (props) => {
  const [isMiniLibraryOpen, setIsMiniLibraryOpen] = useState(false);
  const [isLinkDrawerOpen, setIsLinkDrawerOpen] = useState(false);
  const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);

  const { data: userProfile } = useCurrentUserProfile();

  const { question, quizId } = useQuestion();

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      form.reset();
      mutation.reset();
    }

    props.onOpenChange(isOpen);
  };

  const mutation = useAddEvidenceToQuizResponseMutation();

  const handleMiniLibraryOpenChange = (
    isOpen: boolean,
    data: MiniLibraryOpenChangeData,
  ) => {
    setIsMiniLibraryOpen(isOpen);

    // If the mini library if being closed and has data avaiilable, add the resource to the link
    if (!isOpen && data) {
      addResource(data.id);
    }
  };

  const handleLinkDrawerOpenChange = (
    isOpen: boolean,
    data: OpenChangeData,
  ) => {
    setIsLinkDrawerOpen(isOpen);

    // If the link drawer if being closed and has data avaiilable, add the resource to the link
    if (!isOpen && data) {
      addResource(data.id);
    }
  };

  const handleDocumentDrawerOpenChange = (
    isOpen: boolean,
    data: OpenChangeData,
  ) => {
    setIsDocumentDrawerOpen(isOpen);

    // If the document drawer if being closed and has data avaiilable, add the resource to the link
    if (!isOpen && data) {
      addResource(data.id);
    }
  };

  const form = useForm<z.infer<typeof evidenceSchema>>({
    resolver: zodResolver(evidenceSchema),
    defaultValues: {
      resources: initialResources,
    },
  });

  const {
    fields: resourceFields,
    append: appendResourceField,
    remove: removeResourceField,
  } = useFieldArray({
    name: "resources",
    control: form.control,
  });

  const addResource = (resourceId: string) => {
    // check if resourceId already exists in resourceFields
    if (resourceFields.find((field) => field.resourceId === resourceId)) {
      toast.error("El recurso ya está en la lista");
      return;
    }

    // add resourceId to resources
    appendResourceField({
      resourceId: resourceId,
      reference: "",
      comment: "",
    });
  };

  const removeResource = (resourceId: string) => {
    removeResourceField(
      resourceFields.findIndex(
        (resource) => resource.resourceId === resourceId,
      ),
    );
  };

  const onSubmit = (values: z.infer<typeof evidenceSchema>) => {
    mutation.mutate({
      companyId: userProfile?.companyId || "",
      quizId,
      responseId: props.responseId,
      resources: values.resources,
      dimension: question?.dimension as DimensionId,
      topic: question?.topic || "",
    });
  };

  useEffect(() => {
    if (mutation.isSuccess) {
      handleOpenChange(false);
    }
  }, [mutation]);

  return (
    <>
      <Sheet open={props.isOpen} onOpenChange={handleOpenChange}>
        {Array.isArray(question?.evidences) &&
          question.evidences.length < 3 && (
            <SheetTrigger asChild>
              <Button variant="secondary">
                <SearchZoomIn size={16} /> Añadir evidencia
              </Button>
            </SheetTrigger>
          )}

        <SheetContent className="flex flex-col sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Añadir evidencia</SheetTitle>
            <SheetDescription className="hidden">
              Añade los documentos adjuntos necesarios a tu respuesta.
            </SheetDescription>
          </SheetHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
              <SheetBody>
                {resourceFields.length < 3 && (
                  <div>
                    <div className="mb-2 text-sm">Añade un recurso:</div>

                    <div className="flex flex-row gap-1">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsMiniLibraryOpen(true)}
                      >
                        <SearchNormal1 size={14} />
                        Escoger...
                      </Button>

                      <MiniLibrary
                        isOpen={isMiniLibraryOpen}
                        onOpenChange={handleMiniLibraryOpenChange}
                      />

                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsDocumentDrawerOpen(true)}
                      >
                        <Book size="14" />
                        Nuevo documento
                      </Button>

                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsLinkDrawerOpen(true)}
                      >
                        <Paperclip2 size="14" />
                        Nuevo link
                      </Button>
                    </div>
                  </div>
                )}

                <div className="my-6">
                  {resourceFields.map((field, index) => (
                    <div key={index} className="flex flex-col gap-2 py-4">
                      <div className="flex items-start gap-2">
                        <ResourceDetail
                          className="min-w-0 grow"
                          resourceId={field.resourceId}
                        />

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="mt-1.5 shrink-0"
                          onClick={() => removeResource(field.resourceId)}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>

                      <FormField
                        control={form.control}
                        name={`resources.${index}.reference`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Páginas de referencia</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Por ejemplo: 1, 5, 6..."
                                maxLength={50}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`resources.${index}.comment`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Comentarios adicionales</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Añada aquí cualquier detalle o contexto adicional relevante para esta evidencia..."
                                maxLength={255}
                                rows={3}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}

                  {resourceFields.length === 0 && (
                    <div className="text-sm italic">
                      Agrega al menos un recurso...
                    </div>
                  )}
                </div>
              </SheetBody>

              <SheetFooter className="grid grid-cols-2 items-center">
                <Button
                  type="submit"
                  size="lg"
                  disabled={
                    !(resourceFields.length > 0 && resourceFields.length <= 3)
                  }
                >
                  Guardar
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancelar
                </Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <LinkDrawer
        isOpen={isLinkDrawerOpen}
        onOpenChange={handleLinkDrawerOpenChange}
        addCurrentEditionTag={true}
      />

      <DocumentDrawer
        isOpen={isDocumentDrawerOpen}
        onOpenChange={handleDocumentDrawerOpenChange}
        addCurrentEditionTag={true}
      />
    </>
  );
};

export default NewEvidenceDrawer;
